import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getListMeasurerAction, saveMeasurerAction, updateMeasurerAction, deleteMeasurerAction, getListMeasurerSearch, getNextMeasurerCode } from '../actions/MeasurerAction';
import { getListConsumption, getListConsumptionSearch, getMeasurerAssociateAction } from '../actions/ConsumptionAction';

import { getListClassification } from '../actions/ClassificationAction';

// Hook for fetching abstract classifications list
export const useClassifications = () => {
    return useQuery({
        queryKey: ['classifications'],
        queryFn: async () => {
            const response = await getListClassification();
            return response.data;
        },
        staleTime: 1000 * 60 * 60, // 1 hour cache
    });
};

// Hook for fetching the next auto-generated 
export const useNextMeasurerCode = () => {
    return useQuery({
        queryKey: ['nextMeasurerCode'],
        queryFn: async () => {
            const response = await getNextMeasurerCode();
            return response.data;
        },
        staleTime: 0, // Always fetch fresh to avoid overlapping
    });
};

// Hook for fetching paginated global measurers using standard system search structures
export const useMeasurerSearchData = (paginationModel, sortModel, search, institutionId, showInactive = false) => {
    return useQuery({
        queryKey: ['measurerSearchData', paginationModel.page, paginationModel.pageSize, sortModel, search, institutionId, showInactive],
        queryFn: async () => {
            const request = {
                pageNumber: paginationModel.page + 1,
                pageSize: paginationModel.pageSize,
                institutionId: institutionId,
                text: search,
                sortField: sortModel?.length > 0 ? sortModel[0].field : null,
                sortDirection: sortModel?.length > 0 ? sortModel[0].sort : null,
                showInactive: showInactive,
            };
            const response = await getListMeasurerSearch(request);
            return response.data;
        },
    });
};

// Hook for fetching measurers for an associate
export const useMeasurers = (associateId) => {
    return useQuery({
        queryKey: ['measurers', associateId],
        queryFn: async () => {
            const response = await getListMeasurerAction(associateId);
            return response.data;
        },
        enabled: !!associateId, // Only fetch if associateId is provided
    });
};

// Hook for creating a measurer
export const useCreateMeasurer = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: saveMeasurerAction,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['measurers'] });
            queryClient.invalidateQueries({ queryKey: ['measurerSearchData'] });
        },
    });
};

// Hook for updating a measurer
export const useUpdateMeasurer = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ id, data }) => updateMeasurerAction(id, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['measurers'] });
            queryClient.invalidateQueries({ queryKey: ['measurerSearchData'] });
        },
    });
};

// Hook for deleting a measurer
export const useDeleteMeasurer = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: deleteMeasurerAction,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['measurers'] });
            queryClient.invalidateQueries({ queryKey: ['measurerSearchData'] });
        },
    });
};

// Hook for fetching paginated measurers
export const useMeasurersPaged = (request) => {
    return useQuery({
        queryKey: ['measurersPaged', request?.pageNumber, request?.pageSize, request?.institutionId, request?.isPassive],
        queryFn: () => getListConsumption(request).then(res => res.data),
        enabled: !!request,
    });
};

// Hook for fetching paginated measurers with search text
export const useMeasurersSearch = (request) => {
    return useQuery({
        queryKey: ['measurersSearch', request?.pageNumber, request?.pageSize, request?.institutionId, request?.text, request?.isPassive],
        queryFn: () => getListConsumptionSearch(request).then(res => res.data),
        enabled: !!request && !!request.text,
    });
};

// Hook for fetching a measurer by code
export const useMeasurerByCode = (code) => {
    return useQuery({
        queryKey: ['measurerByCode', code],
        queryFn: () => getMeasurerAssociateAction(code).then(res => res.data),
        enabled: !!code,
    });
};
